import React from 'react';
import { Link } from 'react-router-dom';
import { siteConfig } from '../../data/mock';

/**
 * TarifsTable - Standard pricing table with TTC + crédit d'impôt
 * Usage: <TarifsTable services={['tonte', 'haies', 'debroussaillage']} showDechets={true} />
 */
export const TarifsTable = ({ services = ['tonte', 'haies', 'debroussaillage', 'dechets'], compact = false }) => {
  const { pricing } = siteConfig;
  
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr className="bg-emerald-700 text-white">
            <th className="px-4 py-3 text-left font-semibold">Prestation</th>
            <th className="px-4 py-3 text-right font-semibold">Tarif TTC</th>
            <th className="px-4 py-3 text-right font-semibold text-lime-300">≈ Après crédit 50%</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {services.includes('tonte') && (
            <tr className="hover:bg-emerald-50 transition-colors">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">Tonte de pelouse</div>
                {!compact && <div className="text-sm text-gray-500">Pelouse entretenue ≤ 1 000 m²</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">{pricing.tonte.price}</td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{pricing.tonte.priceAfterCredit}</td>
            </tr>
          )}
          {services.includes('haies') && (
            <>
              <tr className="hover:bg-emerald-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">Taille de haies ≤ 1,5 m</div>
                  {!compact && <div className="text-sm text-gray-500">2 faces + dessus</div>}
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">5 €/m</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">2,50 €/m</td>
              </tr>
              <tr className="hover:bg-emerald-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">Taille de haies 1,5 - 2,5 m</div>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">7,50 €/m</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">3,75 €/m</td>
              </tr>
              <tr className="hover:bg-emerald-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">Taille de haies &gt; 2,5 m</div>
                </td>
                <td className="px-4 py-3 text-right font-semibold text-gray-900">12 €/m</td>
                <td className="px-4 py-3 text-right font-bold text-emerald-600">6 €/m</td>
              </tr>
            </>
          )}
          {services.includes('debroussaillage') && (
            <tr className="hover:bg-emerald-50 transition-colors">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">Débroussaillage / Fauchage</div>
                {!compact && <div className="text-sm text-gray-500">Terrain non entretenu</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">{pricing.debroussaillage.price}</td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{pricing.debroussaillage.priceAfterCredit}</td>
            </tr>
          )}
          {services.includes('vigilance') && (
            <tr className="hover:bg-emerald-50 transition-colors">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">Vigilance résidence</div>
                {!compact && <div className="text-sm text-gray-500">Vérification + photos</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">{pricing.vigilance.price}</td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{pricing.vigilance.priceAfterCredit}</td>
            </tr>
          )}
          {services.includes('bricolage') && (
            <tr className="hover:bg-emerald-50 transition-colors">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">Petit bricolage SAP</div>
                {!compact && <div className="text-sm text-gray-500">Interventions ≤ 2h</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">{pricing.bricolage.price}</td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{pricing.bricolage.priceAfterCredit}</td>
            </tr>
          )}
          {services.includes('dechets') && (
            <tr className="bg-amber-50 hover:bg-amber-100 transition-colors">
              <td className="px-4 py-3">
                <div className="font-medium text-gray-900">Évacuation déchets verts</div>
                {!compact && <div className="text-sm text-gray-500">~1 000 L par trajet</div>}
              </td>
              <td className="px-4 py-3 text-right font-semibold text-gray-900">{pricing.dechets.price}</td>
              <td className="px-4 py-3 text-right font-bold text-emerald-600">{pricing.dechets.priceAfterCredit}</td>
            </tr>
          )}
        </tbody>
      </table>
      <p className="text-xs text-gray-500 mt-3 text-center">
        Tous les tarifs sont en TTC. Le crédit d'impôt de 50% s'applique aux particuliers (Service à la Personne déclaré).
      </p>
    </div>
  );
};

/**
 * ZonesBlock - Town links grid for internal linking
 * Usage: <ZonesBlock limit={12} showViewAll={true} />
 */
export const ZonesBlock = ({ limit = 31, showViewAll = true, currentTown = null }) => {
  const { serviceArea } = siteConfig;
  const towns = serviceArea.cities.filter(t => t !== currentTown).slice(0, limit);
  
  const createSlug = (town) => {
    return town
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
        {towns.map((town) => (
          <Link
            key={town}
            to={`/zone/${createSlug(town)}`}
            className="bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-center hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-700 transition-all"
          >
            {town}
          </Link>
        ))}
      </div>
      {showViewAll && limit < 31 && (
        <div className="text-center mt-4">
          <Link 
            to="/zone" 
            className="text-emerald-600 hover:text-emerald-700 font-medium text-sm"
          >
            Voir les 31 communes →
          </Link>
        </div>
      )}
    </div>
  );
};

/**
 * PrestationsGrid - Service cards linking to service pages
 * Usage: <PrestationsGrid services={['tonte', 'haies']} town="Coëx" />
 */
export const PrestationsGrid = ({ services = null, town = null, compact = false }) => {
  const allServices = siteConfig.services;
  const displayServices = services 
    ? allServices.filter(s => services.includes(s.id))
    : allServices;

  const createSlug = (t) => {
    return t?.toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  return (
    <div className={`grid ${compact ? 'grid-cols-2 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-4`}>
      {displayServices.map((service) => {
        const link = town 
          ? `/services/${service.id}/${createSlug(town)}`
          : `/services/${service.id}`;
        
        return (
          <Link
            key={service.id}
            to={link}
            className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-lg hover:border-emerald-300 transition-all group"
          >
            <h3 className={`font-bold text-gray-900 group-hover:text-emerald-700 transition-colors ${compact ? 'text-base' : 'text-lg'} mb-2`}>
              {service.title}
            </h3>
            {!compact && (
              <p className="text-sm text-gray-600 mb-3">{service.subtitle}</p>
            )}
            <span className="text-emerald-600 text-sm font-medium group-hover:translate-x-1 inline-block transition-transform">
              En savoir plus →
            </span>
          </Link>
        );
      })}
    </div>
  );
};

/**
 * CTABox - Consistent call-to-action block
 * Usage: <CTABox variant="primary" town="Saint-Gilles" />
 */
export const CTABox = ({ variant = 'primary', town = null, showWhatsApp = true }) => {
  const { contact } = siteConfig;
  const locationText = town ? `à ${town}` : 'en Vendée';

  if (variant === 'inline') {
    return (
      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/devis"
          className="inline-flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-6 rounded-xl transition-all shadow-lg hover:shadow-xl"
        >
          Obtenir un devis express
        </Link>
        {showWhatsApp && (
          <a
            href={`https://wa.me/${contact.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition-all"
          >
            WhatsApp
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-2xl p-8 text-white text-center">
      <h3 className="text-2xl font-bold mb-3">
        Besoin d'un jardinier {locationText} ?
      </h3>
      <p className="text-emerald-100 mb-6 max-w-xl mx-auto">
        Devis gratuit sous 24h. Crédit d'impôt 50% (Service à la Personne déclaré).
      </p>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Link
          to="/devis"
          className="inline-flex items-center justify-center bg-white text-emerald-700 hover:bg-emerald-50 font-bold py-3 px-8 rounded-xl transition-all shadow-lg"
        >
          Obtenir un devis express
        </Link>
        {showWhatsApp && (
          <a
            href={`https://wa.me/${contact.whatsapp}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-xl transition-all"
          >
            WhatsApp
          </a>
        )}
      </div>
      <p className="text-xs text-emerald-200 mt-4">
        📞 {contact.phone} • Lun, Mar, Jeu, Ven 11h–17h
      </p>
    </div>
  );
};

/**
 * AvoirAussi - "À voir aussi" internal links block
 * Usage: <AvoirAussi links={[{to: '/services/tonte', label: 'Tonte'}]} />
 */
export const AvoirAussi = ({ links = [], currentPage = null }) => {
  const defaultLinks = [
    { to: '/services/tonte', label: 'Tonte de pelouse' },
    { to: '/services/haies', label: 'Taille de haies' },
    { to: '/services/debroussaillage', label: 'Débroussaillage' },
    { to: '/mon-parcours', label: 'Mon parcours' },
    { to: '/tarifs', label: 'Tous les tarifs' },
    { to: '/zone', label: 'Zones d\'intervention' },
    { to: '/devis', label: 'Demander un devis' },
    { to: '/faq', label: 'Questions fréquentes' },
    { to: '/contact', label: 'Contact' }
  ];

  const displayLinks = links.length > 0 
    ? links 
    : defaultLinks.filter(l => l.to !== currentPage).slice(0, 6);

  return (
    <div className="bg-gray-50 rounded-xl p-6">
      <h3 className="text-lg font-bold text-gray-900 mb-4">À voir aussi</h3>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {displayLinks.map((link, i) => (
          <Link
            key={i}
            to={link.to}
            className="text-emerald-600 hover:text-emerald-700 hover:underline text-sm font-medium"
          >
            {link.label} →
          </Link>
        ))}
      </div>
    </div>
  );
};

/**
 * MiniFAQ - Reusable FAQ section with accordion
 * Usage: <MiniFAQ questions={[{q: 'Question?', a: 'Answer'}]} />
 */
export const MiniFAQ = ({ questions = [], title = "Questions fréquentes" }) => {
  const [openIndex, setOpenIndex] = React.useState(null);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">{title}</h2>
      <div className="space-y-3">
        {questions.map((item, index) => (
          <div 
            key={index}
            className="bg-white border border-gray-200 rounded-xl overflow-hidden"
          >
            <button
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <span className="font-medium text-gray-900 pr-4">{item.q}</span>
              <span className={`text-emerald-600 transition-transform ${openIndex === index ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
            {openIndex === index && (
              <div className="px-5 pb-4 text-gray-600 border-t border-gray-100 pt-3">
                {item.a}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * ExclusionsList - Services NOT provided (legal clarity)
 */
export const ExclusionsList = ({ compact = false }) => {
  const exclusions = [
    'Plomberie',
    'Électricité', 
    'Chauffage/gaz',
    'Gros travaux/bâtiment',
    'Élagage à risque',
    'Débarras/déménagement',
    'Traitements phytosanitaires interdits'
  ];

  if (compact) {
    return (
      <p className="text-xs text-gray-500">
        Non inclus : {exclusions.join(' • ')}
      </p>
    );
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
      <h4 className="font-semibold text-red-800 mb-2">Services non proposés :</h4>
      <ul className="grid grid-cols-2 md:grid-cols-3 gap-1 text-sm text-red-700">
        {exclusions.map((item, i) => (
          <li key={i}>❌ {item}</li>
        ))}
      </ul>
    </div>
  );
};

export default {
  TarifsTable,
  ZonesBlock,
  PrestationsGrid,
  CTABox,
  AvoirAussi,
  MiniFAQ,
  ExclusionsList
};
